import { AudioMutedOutlined, EyeOutlined, LoadingOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Dropdown, MenuProps, Space, Tooltip } from "antd";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import styled from "styled-components";
import useQuickResponses from "../../hooks/useQuickResponses";
import AnimatedAudioIcon from "./AnimatedAudioIcon";
import IconBox from "./IconBox";

export interface InputAddonsProps {
  isChatOnlyMode?: boolean;
  isEditingTranscript?: boolean;
  isAgentSpeaking: boolean;
  showQuickResponses: boolean;
  showSuggestedAnswer: boolean;
  onShowQuickResponses: () => void;
  onShowSuggestedAnswer: () => void;
}

export default function InputAddons({ isChatOnlyMode, isEditingTranscript, isAgentSpeaking, showQuickResponses, showSuggestedAnswer, onShowQuickResponses, onShowSuggestedAnswer }: InputAddonsProps) {
  const { t } = useTranslation();

  const { quickResponses, processingQuickResponseId, sendQuickResponse } = useQuickResponses();

  const menuProps = useMemo<MenuProps>(
    () => {
      const quickResponseItems: MenuProps["items"] = quickResponses.map(qr => ({
        key: qr.id,
        label: (
          <Space>
            {processingQuickResponseId === qr.id && <LoadingOutlined />}
            <Trans i18nKey="agentAssist.quickResponseX" values={{ value: qr.displayName }} />
          </Space>
        ),
        disabled: processingQuickResponseId !== null,
        onClick: () => sendQuickResponse(qr.id),
      }));

      const hiddenItems: MenuProps["items"] = [];

      if (!showQuickResponses && quickResponses.length > 0) {
        hiddenItems.push({
          key: "showQuickResponses",
          label: (
            <Space>
              <EyeOutlined />
              <span>{t("agentAssist.showQuickResponses")}</span>
            </Space>
          ),
          onClick: () => { onShowQuickResponses(); },
        });
      }

      if (!showSuggestedAnswer) {
        hiddenItems.push({
          key: "showSuggestedAnswer",
          label: (
            <Space>
              <EyeOutlined />
              <span>{t("agentAssist.showSuggestedAnswer")}</span>
            </Space>
          ),
          onClick: () => { onShowSuggestedAnswer(); },
        });
      }

      const items: MenuProps["items"] = [
        ...quickResponseItems,
        ...quickResponseItems.length > 0 && hiddenItems.length > 0 ? [{ type: "divider" as const }] : [],
        ...hiddenItems,
      ];

      return { items };
    },
    [onShowQuickResponses, onShowSuggestedAnswer, processingQuickResponseId, quickResponses, sendQuickResponse, showQuickResponses, showSuggestedAnswer, t],
  );

  const audioIconTooltip = isChatOnlyMode
    ? t("input.chatOnlyModeHint")
    : isEditingTranscript
      ? t("input.mutedWhileTypingHint")
      : isAgentSpeaking
        ? t("input.listeningHint")
        : t("input.voiceRecognitionActiveHint");

  const hasHiddenSuggestions = !showQuickResponses || !showSuggestedAnswer;
  const showMenu = (!showQuickResponses && quickResponses.length > 0) || hasHiddenSuggestions;

  return (
    <AddonBox>
      <Tooltip title={audioIconTooltip}>
        <IconBox $colorTokenKey={isChatOnlyMode ? "colorError" : "colorTextSecondary"}>
          {
            (isChatOnlyMode || isEditingTranscript)
              ? <AudioMutedOutlined />
              : <AnimatedAudioIcon isAgentSpeaking={isAgentSpeaking} />
          }
        </IconBox>
      </Tooltip>
      {
        showMenu && // not show === banished to input toolbar
        <Dropdown
          menu={menuProps}
          trigger={["click"]}
          placement="topLeft"
          disabled={processingQuickResponseId !== null}
        >
          <Button
            type="link"
            icon={
              processingQuickResponseId
                ? <LoadingOutlined />
                : <PlusCircleOutlined />
            }
            aria-label={t("agentAssist.showQuickResponses")}
            style={{ fontSize: "1.1em" }}
          />
        </Dropdown>
      }
    </AddonBox>
  );
}

const AddonBox = styled.div`
  display: flex;
  align-items: center;
`;
